import type { ToolDefinition } from "@arcadeai/arcadejs/resources/index.mjs";

export type Mode = "single" | "multi";
export type McpSearchScope = "servers" | "tools";

/** Catalog tool row — alias of the Arcade API `ToolDefinition`. */
export type McpTool = ToolDefinition;

export type McpServer = {
  /** Stable id (e.g. "Gmail", "GoogleDrive"). */
  name: string;
  /** User-visible label. Caller is responsible for resolving from its catalog. */
  displayName: string;
  description?: string;
  /**
   * Set only for toolkits the catalog explicitly knows are not-yet-optimized
   * Arcade starters ("arcade starter" badge). Interpret via `isUnoptimizedServer`
   * rather than reading this field directly — the absence of the flag is
   * deliberately meaningful (see that helper).
   */
  isUnoptimized?: boolean;
  version?: string;
  /** Every tool the server exposes. Loaded up front by the caller. */
  tools: McpTool[];
};

/**
 * A server counts as "unoptimized" (and is tucked behind the starter reveal)
 * ONLY when the catalog explicitly flags it. Anything without the flag — a
 * user-added server, an external MCP server, anything we have no design-system
 * metadata for — is treated as optimized and shown as a first-class result.
 *
 * The default is intentionally permissive: we would rather surface an unknown
 * server than bury a user's own one, so a mapper that omits the flag fails open
 * (visible), never closed (hidden). Keep this as the single source of the rule
 * so the `=== true` intent isn't re-derived as a scattered truthy check.
 */
export const isUnoptimizedServer = (server: McpServer): boolean =>
  server.isUnoptimized === true;

/** A server paired with a subset of its tools (search results, selection, etc.). */
export type ToolGroup = { server: McpServer; tools: McpTool[] };

/** Chip shape consumed by `McpSelectionSummaryBar` and `McpServerAvatarStack`. */
export type SelectionChip = {
  id: string;
  name: string;
  toolkitId: string;
  selectedCount: number;
  total: number;
  isPartial: boolean;
};
